/**
 * Drift Detection Panel Component
 *
 * DC-704: Displays drift analysis results comparing baseline vs current data.
 * Shows PSI and K-S test results with visual severity indicators.
 *
 * @see Documentation/Development/Design Docs/Missing_Accordion_Capabilities_Design.md
 */

import React, { useState, useEffect, useRef } from 'react';
import './drift-detection-panel.css';

// Types matching server/dataQuality/driftDetection.ts
type DriftSeverity = 'none' | 'minor' | 'moderate' | 'significant';

interface BinContribution {
  binIndex: number;
  binRange: string;
  baselinePercent: number;
  currentPercent: number;
  contribution: number;
}

interface PSIResult {
  psi: number;
  severity: DriftSeverity;
  binContributions: BinContribution[];
  interpretation: string;
}

interface KSTestResult {
  statistic: number;
  pValue: number;
  driftDetected: boolean;
  interpretation: string;
}

interface ColumnDriftResult {
  column: string;
  dataType: 'numeric' | 'categorical';
  psi?: PSIResult;
  ksTest?: KSTestResult;
  overallDrift: DriftSeverity;
  recommendation: string;
}

interface DriftReport {
  analyzedAt: string;
  baselineId: string;
  baselineCreatedAt: string;
  columnsAnalyzed: number;
  columnsWithDrift: number;
  overallDriftScore: number;
  overallSeverity: DriftSeverity;
  columnResults: ColumnDriftResult[];
  summary: string;
}

interface DriftBaseline {
  baseline_id: string;
  name: string;
  description?: string;
  row_count: number;
  column_count: number;
  is_active: boolean;
  created_at: string;
}

interface DriftDetectionPanelProps {
  journeyId: string;
  onBaselineCreated?: (baseline: DriftBaseline) => void;
  onAnalysisComplete?: (report: DriftReport) => void;
}

const SEVERITY_LABELS: Record<DriftSeverity, string> = {
  none: 'No Drift',
  minor: 'Minor Drift',
  moderate: 'Moderate Drift',
  significant: 'Significant Drift'
};

const DriftDetectionPanel: React.FC<DriftDetectionPanelProps> = ({
  journeyId,
  onBaselineCreated,
  onAnalysisComplete
}) => {
  const [baselines, setBaselines] = useState<DriftBaseline[]>([]);
  const [activeBaseline, setActiveBaseline] = useState<DriftBaseline | null>(null);
  const [latestResult, setLatestResult] = useState<DriftReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedColumn, setExpandedColumn] = useState<string | null>(null);
  const [showCreateBaseline, setShowCreateBaseline] = useState(false);
  const [baselineName, setBaselineName] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const analyzeFileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchData is defined in component
  }, [journeyId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch baselines and latest result in parallel
      const [baselinesRes, latestRes] = await Promise.all([
        fetch(`/api/data-quality/drift/baselines/${journeyId}`),
        fetch(`/api/data-quality/drift/results/${journeyId}/latest`)
      ]);

      if (baselinesRes.ok) {
        const data = await baselinesRes.json();
        setBaselines(data.baselines || []);
        const active = (data.baselines || []).find((b: DriftBaseline) => b.is_active);
        setActiveBaseline(active || null);
      }

      if (latestRes.ok) {
        const data = await latestRes.json();
        setLatestResult(data.result || null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load drift data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBaseline = async (file: File) => {
    if (!baselineName.trim()) {
      setError('Please enter a baseline name');
      return;
    }

    try {
      setAnalyzing(true);
      setError(null);

      const formData = new FormData();
      formData.append('file', file);
      formData.append('journey_id', journeyId);
      formData.append('name', baselineName);

      const response = await fetch('/api/data-quality/drift/baselines', {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to create baseline');
      }

      setBaselines([data.baseline, ...baselines]);
      setActiveBaseline(data.baseline);
      setShowCreateBaseline(false);
      setBaselineName('');
      onBaselineCreated?.(data.baseline);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create baseline');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleAnalyzeDrift = async (file: File) => {
    if (!activeBaseline) {
      setError('No active baseline. Create a baseline first.');
      return;
    }

    try {
      setAnalyzing(true);
      setError(null);

      const formData = new FormData();
      formData.append('file', file);
      formData.append('journey_id', journeyId);
      formData.append('baseline_id', activeBaseline.baseline_id);

      const response = await fetch('/api/data-quality/drift/analyze', {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to analyze drift');
      }

      setLatestResult(data.report);
      onAnalysisComplete?.(data.report);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to analyze drift');
    } finally {
      setAnalyzing(false);
    }
  };

  const getSeverityClass = (severity: DriftSeverity): string => {
    return `drift-severity--${severity}`;
  };

  if (loading) {
    return (
      <div className="drift-panel drift-panel--loading">
        <div className="drift-panel__header">
          <h3>Drift Detection</h3>
        </div>
        <div className="drift-panel__loading">
          <div className="spinner" />
          <span>Loading drift data...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="drift-panel">
      {/* Header */}
      <div className="drift-panel__header">
        <div className="drift-panel__title">
          <h3>Drift Detection</h3>
          {activeBaseline && (
            <span className="drift-panel__baseline-badge">
              Baseline: {activeBaseline.name}
            </span>
          )}
        </div>
        <div className="drift-panel__actions">
          {!activeBaseline ? (
            <button
              className="drift-panel__btn drift-panel__btn--primary"
              onClick={() => setShowCreateBaseline(true)}
            >
              Create Baseline
            </button>
          ) : (
            <>
              <button
                className="drift-panel__btn drift-panel__btn--secondary"
                onClick={() => setShowCreateBaseline(true)}
              >
                New Baseline
              </button>
              <button
                className="drift-panel__btn drift-panel__btn--primary"
                onClick={() => analyzeFileInputRef.current?.click()}
                disabled={analyzing}
              >
                {analyzing ? 'Analyzing...' : 'Analyze Drift'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="drift-panel__error">
          <span>{error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Create Baseline Modal */}
      {showCreateBaseline && (
        <div className="drift-panel__modal">
          <div className="drift-panel__modal-content">
            <h4>Create Baseline</h4>
            <p>Upload a dataset to establish as the baseline for drift comparison.</p>
            <input
              type="text"
              placeholder="Baseline name (e.g., 'Q4 2024 Production')"
              value={baselineName}
              onChange={(e) => setBaselineName(e.target.value)}
              className="drift-panel__input"
            />
            <div className="drift-panel__modal-actions">
              <button
                className="drift-panel__btn drift-panel__btn--secondary"
                onClick={() => setShowCreateBaseline(false)}
              >
                Cancel
              </button>
              <button
                className="drift-panel__btn drift-panel__btn--primary"
                onClick={() => fileInputRef.current?.click()}
                disabled={!baselineName.trim()}
              >
                Select File
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hidden file inputs */}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept=".csv,.tsv,.xlsx,.xls"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleCreateBaseline(file);
          e.target.value = '';
        }}
      />
      <input
        type="file"
        ref={analyzeFileInputRef}
        style={{ display: 'none' }}
        accept=".csv,.tsv,.xlsx,.xls"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleAnalyzeDrift(file);
          e.target.value = '';
        }}
      />

      {/* No Baseline State */}
      {!activeBaseline && !showCreateBaseline && (
        <div className="drift-panel__empty">
          <div className="drift-panel__empty-icon">📊</div>
          <h4>No Baseline Configured</h4>
          <p>Create a baseline from your reference dataset to start detecting drift.</p>
        </div>
      )}

      {/* Results Display */}
      {latestResult && (
        <div className="drift-panel__results">
          {/* Summary */}
          <div className="drift-panel__summary">
            <div className="drift-panel__score">
              <div
                className={`drift-panel__score-value ${getSeverityClass(latestResult.overallSeverity)}`}
              >
                {latestResult.overallDriftScore.toFixed(1)}
              </div>
              <div className="drift-panel__score-label">Drift Score</div>
            </div>
            <div className="drift-panel__stats">
              <div className="drift-panel__stat">
                <span className="drift-panel__stat-value">{latestResult.columnsAnalyzed}</span>
                <span className="drift-panel__stat-label">Columns Analyzed</span>
              </div>
              <div className="drift-panel__stat">
                <span className={`drift-panel__stat-value ${latestResult.columnsWithDrift > 0 ? 'drift-severity--moderate' : ''}`}>
                  {latestResult.columnsWithDrift}
                </span>
                <span className="drift-panel__stat-label">With Drift</span>
              </div>
              <div className="drift-panel__stat">
                <span className={`drift-panel__stat-value ${getSeverityClass(latestResult.overallSeverity)}`}>
                  {SEVERITY_LABELS[latestResult.overallSeverity]}
                </span>
                <span className="drift-panel__stat-label">Overall Status</span>
              </div>
            </div>
          </div>

          {/* Column Results */}
          <div className="drift-panel__columns">
            <h4>Column-Level Analysis</h4>
            <div className="drift-panel__column-list">
              {latestResult.columnResults.map((col) => (
                <div
                  key={col.column}
                  className={`drift-panel__column ${expandedColumn === col.column ? 'drift-panel__column--expanded' : ''}`}
                >
                  <div
                    className="drift-panel__column-header"
                    onClick={() => setExpandedColumn(
                      expandedColumn === col.column ? null : col.column
                    )}
                  >
                    <div className="drift-panel__column-info">
                      <span className="drift-panel__column-name">{col.column}</span>
                      <span className="drift-panel__column-type">{col.dataType}</span>
                    </div>
                    <div className="drift-panel__column-status">
                      <span className={`drift-panel__severity-badge ${getSeverityClass(col.overallDrift)}`}>
                        {SEVERITY_LABELS[col.overallDrift]}
                      </span>
                      <span className="drift-panel__expand-icon">
                        {expandedColumn === col.column ? '▼' : '▶'}
                      </span>
                    </div>
                  </div>

                  {expandedColumn === col.column && (
                    <div className="drift-panel__column-details">
                      {/* PSI Results */}
                      {col.psi && (
                        <div className="drift-panel__metric">
                          <div className="drift-panel__metric-header">
                            <span className="drift-panel__metric-name">PSI</span>
                            <span className={`drift-panel__metric-value ${getSeverityClass(col.psi.severity)}`}>
                              {col.psi.psi.toFixed(4)}
                            </span>
                          </div>
                          <p className="drift-panel__interpretation">{col.psi.interpretation}</p>

                          {/* PSI Bin Chart */}
                          {col.psi.binContributions.length > 0 && (
                            <div className="drift-panel__chart">
                              {col.psi.binContributions.map((bin) => (
                                <div key={bin.binIndex} className="drift-panel__bar-group">
                                  <div className="drift-panel__bar-container">
                                    <div
                                      className="drift-panel__bar drift-panel__bar--baseline"
                                      style={{ height: `${Math.min(bin.baselinePercent * 2, 100)}%` }}
                                      title={`Baseline: ${bin.baselinePercent.toFixed(1)}%`}
                                    />
                                    <div
                                      className="drift-panel__bar drift-panel__bar--current"
                                      style={{ height: `${Math.min(bin.currentPercent * 2, 100)}%` }}
                                      title={`Current: ${bin.currentPercent.toFixed(1)}%`}
                                    />
                                  </div>
                                  <span className="drift-panel__bar-label" title={bin.binRange}>
                                    {bin.binIndex + 1}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {/* K-S Test Results */}
                      {col.ksTest && (
                        <div className="drift-panel__metric">
                          <div className="drift-panel__metric-header">
                            <span className="drift-panel__metric-name">K-S Test</span>
                            <span className={`drift-panel__metric-value ${col.ksTest.driftDetected ? 'drift-severity--significant' : 'drift-severity--none'}`}>
                              D = {col.ksTest.statistic.toFixed(4)}
                            </span>
                          </div>
                          <p className="drift-panel__interpretation">
                            p-value: {col.ksTest.pValue.toFixed(4)} - {col.ksTest.interpretation}
                          </p>
                        </div>
                      )}

                      {/* Recommendation */}
                      <div className="drift-panel__recommendation">
                        <strong>Recommendation:</strong> {col.recommendation}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Timestamp */}
          <div className="drift-panel__footer">
            <span>Analyzed: {new Date(latestResult.analyzedAt).toLocaleString()}</span>
            <span>Baseline from: {new Date(latestResult.baselineCreatedAt).toLocaleString()}</span>
          </div>
        </div>
      )}

      {/* No Results Yet */}
      {activeBaseline && !latestResult && (
        <div className="drift-panel__empty">
          <div className="drift-panel__empty-icon">✓</div>
          <h4>Baseline Ready</h4>
          <p>Upload a new dataset to compare against the baseline and detect drift.</p>
        </div>
      )}
    </div>
  );
};

export default DriftDetectionPanel;
